<template>
    <VVVLayoutNew>
        <template #page-title>
            Home
        </template>
        <MDBAlert v-if="isUser && userDetail.isNewUser && revenueAlert" color="warning" static class="mb-2">
            <div class="alert-container">
                <vue-feather class="mx-1" type="alert-circle" :stroke-width="2.5" />
                <p class="mb-0 alert-message">
                    Welcome, <strong style="text-transform: capitalize;">{{ userDetail.firstName }}</strong>! Your first
                    earnings
                    report will be live in about 30-days. <a href="//CreatorShield.com/support" target="_blank"
                        rel="noopener noreferrer">See the earnings
                        schedule</a>.
                </p>
                <vue-feather type="x" :stroke-width="2.5" size="18" class="ms-auto" @click="revenueAlert = false"
                    style="cursor: pointer;" />
            </div>
        </MDBAlert>
        <MDBAlert v-if="googleSheetAlert" static class="mb-4" style="background-color: #D7DEFF; color: var(--primary);">
            <div class="alert-container">
                <vue-feather class="mx-1" type="alert-circle" :stroke-width="2.5" />
                <p class="mb-0 alert-message">
                    Welcome to our refreshed dashboard.
                    <a href="https://docs.google.com/forms/d/e/1FAIpQLSfpkfJTFTKfC_gXKUhFUdjUuTXBuE4BZKR5TZg5jw07BRQ96g/viewform"
                        target="_blank" rel="noopener noreferrer" class="text-decoration-underline fw-bold"
                        style="color: var(--primary);">
                        Send us feedback on how to improve.
                    </a>
                </p>
                <vue-feather type="x" :stroke-width="2.5" size="18" class="ms-auto" @click="googleSheetAlert = false"
                    style="cursor: pointer;" />
            </div>
        </MDBAlert>
        <MDBAlert
            v-if="isImpersonating"
            color="info"
            static
            class="mb-3 d-flex align-items-center justify-content-between"
            style="background-color: #D7DEFF; color: var(--primary); font-weight: 600;"
            >
            <div class="d-flex align-items-center">
                <vue-feather class="mx-2" type="user" :stroke-width="2.5" />
                <span>
                Viewing as
                <b>{{ userImpersonateDetail.firstName }} {{ userImpersonateDetail.lastName }}</b>
                ({{ userImpersonateDetail.email }})
                [IMPERSONATING]
                </span>
            </div>

            <MDBBtn
            color="danger"
            size="sm"
            class="fw-bold"
            @click="stopImpersonation"
            style="cursor: pointer;"
            >
                Stop Impersonation
            </MDBBtn>
        </MDBAlert>
        <div class="mb-3 mb-md-4 gap-3 metrics-container">
            <MDBCard class="shadow-4 rounded-5 px-4">
                <MDBCardHeader class="px-0">
                    <span class="fs-5 fe-bold" style="color: var(--primary); font-weight: bold;">
                        Your Overview
                    </span>
                </MDBCardHeader>
                <MDBCardBody class="p-0 pb-1">
                    <MDBListGroup class="my-3">
                        <MDBListGroupItem class="border my-2 rounded-3 px-3 metric-row">
                            <span>Total Revenue Generated</span>
                            <span v-if="isRevenueLoading" class="metric-value-skeleton" aria-label="Loading" />
                            <span v-else class="metric-value">${{ totalRevenue ?? '0' }}</span>
                        </MDBListGroupItem>
                        <MDBListGroupItem class="border my-2 rounded-3 px-3 metric-row">
                            <span>Revenues Generated Last Month</span>
                            <span v-if="isRevenueLoading" class="metric-value-skeleton" aria-label="Loading" />
                            <span v-else class="metric-value">${{ revenueLastMonth ?? '0' }}</span>
                        </MDBListGroupItem>
                        <MDBListGroupItem class="border my-2 rounded-3 px-3 metric-row">
                            <span>Total Active Claims</span>
                            <span v-if="loading" class="metric-value-skeleton" aria-label="Loading" />
                            <span v-else class="metric-value">{{ activeClaimsCount }}</span>
                        </MDBListGroupItem>
                        <MDBListGroupItem class="border my-2 rounded-3 px-3 metric-row">
                            <span>Pending Claims</span>
                            <span v-if="isOverviewStatsLoading" class="metric-value-skeleton" aria-label="Loading" />
                            <span v-else class="metric-value">{{ pendingClaims }}</span>
                        </MDBListGroupItem>
                        <MDBListGroupItem v-if="isAdminOrSuperAdmin"
                            class="border my-2 rounded-3 px-3 metric-row">
                            <span>New Claims Approval % (30 days)</span>
                            <span v-if="isOverviewStatsLoading" class="metric-value-skeleton" aria-label="Loading" />
                            <span v-else class="metric-value">{{ approvalRatePercentage }}%</span>
                        </MDBListGroupItem>
                        <MDBListGroupItem v-if="isAdminOrSuperAdmin"
                            class="border my-2 rounded-3 px-3 metric-row">
                            <span style="font-size: 12px;">Avg Claim Issuance Time (30 days)</span>
                            <span v-if="isOverviewStatsLoading" class="metric-value-skeleton" aria-label="Loading" />
                            <span v-else class="metric-value">{{ averageIssuanceTimeDisplay }}</span>
                        </MDBListGroupItem>
                    </MDBListGroup>
                </MDBCardBody>
            </MDBCard>
            <MDBCard class="shadow-4 rounded-5 px-4">
                <MDBCardHeader class="px-0">
                    <span class="fs-5 fe-bold" style="color: var(--primary); font-weight: bold;">
                        Issues
                    </span>
                </MDBCardHeader>
                <MDBCardBody class="p-0 pb-1" style="min-height: 300px;">
                    <MDBListGroup class="my-3">
                        <template v-for="counter in issueList" :key="counter.id">
                            <!-- <router-link :to="'/claims-list?claimStatus=' + counter.id" custom
                                v-slot="{ href, navigate }"> -->
                            <router-link 
                                :to="buildClaimsListRoute(counter.id)" 
                                custom
                                v-slot="{ href, navigate }">
                                <MDBListGroupItem class="border my-2 rounded-3 px-3 metric-row" tag="a" :href="href"
                                    @click.stop="navigate" title="View List">
                                    <span>{{ counter.name }}</span>
                                    <span v-if="loading" class="metric-value-skeleton" aria-label="Loading" />
                                    <span v-else class="metric-value">{{ getCount(counter.id) }}</span>
                                </MDBListGroupItem>
                            </router-link>
                        </template>
                    </MDBListGroup>
                </MDBCardBody>
            </MDBCard>
            <MDBCard class="shadow-4 rounded-5 px-4">
                <MDBCardHeader class="d-flex align-items-center justify-content-between px-0">
                    <span class="fs-5 fe-bold" style="color: var(--primary); font-weight: bold;">
                        Latest Tutorial
                    </span>
                    <span @click="toTutorials" class="gap-1 d-flex align-items-center fs-7"
                        style="color: var(--primary); cursor: pointer;">View all tutorials
                        <MDBIcon icon="arrow-right" class="fas fa-fw" />
                    </span>
                </MDBCardHeader>
                <MDBCardBody class="px-0 py-3" style="min-height: 300px;">
                    <div v-if="isTutorialLoading" class="tutorial-loading">
                        <div class="image-container image-container-big">
                            <div class="loader" />
                            <img src="@/assets/VVV/vvv-loader.png" alt="Loading image" />
                        </div>
                    </div>
                    <div v-else-if="!latestTutorial" class="tutorial-empty">
                        Uploading Soon!
                    </div>
                    <template v-else-if="latestTutorial?.link">
                            <iframe width="100%" height="250" :src="getYoutubeVideoIframe(latestTutorial.link)"
                                frameborder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowfullscreen></iframe>
                            <div class="pt-2">
                                <p class="text-center fw-bold" style="font-size: 16px;">
                                    {{ latestTutorial.title }}
                                </p>
                                <p class="text-left mt-2" style="font-size: 14px;">
                                    {{ latestTutorial.viewDescription }}
                                </p>
                                <!-- <MDBBtn class="btn-primary" @click="toTutorials">
                                    View All Tutorials
                                    <MDBIcon icon="arrow-right" class="fas fa-fw" />
                                </MDBBtn> -->
                            </div>
                    </template>
                </MDBCardBody>
            </MDBCard>
        </div>
        <MDBCard class="shadow-4 rounded-5">
            <MDBCardHeader
                class="text-left justify-content-between align-items-center bg-white d-block d-md-flex border-0">
                <span class="fs-5 fe-bold" style="color: var(--primary); font-weight: bold;">
                    Recently Added Claims
                </span>
                <!-- <MDBBtn class="btn-primary mt-md-0 mt-3 fw-normal" @click="toClaimsList">
                    View Full List
                    <MDBIcon icon="arrow-right" class="fas fa-fw" />
                </MDBBtn> -->
                <span @click="toClaimsList" class="gap-1 d-flex align-items-center fs-7"
                    style="color: var(--primary); cursor: pointer;">View full list
                    <MDBIcon icon="arrow-right" class="fas fa-fw" />
                </span>
            </MDBCardHeader>
            <MDBCardBody class="overflow-auto justify-content-start p-0">
                <MDBTable hover class="align-middle">
                    <thead class="table-light" style="z-index: 0;">
                        <tr>
                            <th scope="col" class="text-uppercase align-middle text-left" v-if="isAdminOrSuperAdmin">
                                Distributor
                            </th>
                            <th v-for="(header, i) in tableHeaders" :key="i" scope="col"
                                class=" align-middle text-left text-uppercase">
                                {{ header }}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="overflow-auto">
                        <template v-if="isLoading">
                            <tr>
                                <td :colspan="isAdminOrSuperAdmin ? '7' : '6'" class="text-center">
                                    <div
                                        class="image-container image-container-big d-flex justify-content-center align-items-center">
                                        <div class="loader" />
                                        <img src="@/assets/VVV/vvv-loader.png" alt="Loading image" />
                                    </div>
                                </td>
                            </tr>
                        </template>
                        <template v-else>
                            <template v-if="contentsList.length === 0">
                                <tr>
                                    <td :colspan="isAdminOrSuperAdmin ? '7' : '6'" class="text-center">No claims found.
                                    </td>
                                </tr>
                            </template>
                            <template v-else v-for="(contents, k) in contentsList" :key="k">
                                <tr>
                                    <td v-if="isAdminOrSuperAdmin" class="text-truncate">
                                        <div class="d-flex align-items-center gap-2">
                                            <span>
                                                {{
                                                    contents.userId ? contents.userFirstName + ' ' + contents.userLastName :
                                                        '—'
                                                }}
                                            </span>
                                            <span v-if="contents.assetLabel" @click.stop="copy(contents.assetLabel)"
                                                class="px-3 d-flex align-items-center rounded-4 py-1"
                                                style="background: var(--primary); color: white; font-size:12px; border-radius: 10px;cursor: pointer;"
                                                title="Click to Copy Asset Label">
                                                {{ contents.assetLabel }}
                                                <vue-feather type="copy" size="14" :stroke-width="2.5" class="ms-2" />
                                            </span>
                                        </div>
                                    </td>
                                    <td>
                                        <div class="d-flex flex-column justify-content-between">
                                            <div>
                                                <MDBBadge v-if="contents.claimStatusName"
                                                    :color="setBadgeColor(contents.claimStatus)"
                                                    class="rounded-circle me-2" dot
                                                    style="position: relative;margin-left:0;"></MDBBadge>
                                                <span class="fw-bold">
                                                    {{ contents.claimStatusName ? contents.claimStatusName : '—' }}
                                                </span>
                                            </div>
                                            <span v-if="contents.claimStatus === '1' && contents.issuanceTime != 0"
                                                style="color: black;font-size: 12px;" class="ms-3">
                                                {{ `${convertToReadableTime(contents.issuanceTime)} claim time` }}
                                            </span>
                                            <span v-if="contents.claimStatus === '5'"
                                                style="color: black;font-size: 12px;" class="ms-3">
                                                {{ timeAgo(contents.dateAdded) }}
                                            </span>
                                            <span v-if="contents.claimStatus === '4'"
                                                style="color: black;font-size: 12px;" class="ms-3">
                                                {{ timeAgo(contents.issueFixDate) }}
                                            </span>
                                        </div>
                                    </td>
                                    <td class="text-truncate" style="max-width: 200px;">
                                        <img v-if="contents.thumbnail" :src="contents.thumbnail" alt="thumbnail"
                                            style="width: 30px; height: 30px" class="rounded-circle me-2" />
                                        <img v-else src="@/assets/user-default.png" alt="thumbnail"
                                            style="width: 30px; height: 30px" class="rounded-circle me-2" />
                                        <span>
                                            {{ contents.youtubeChannel }}
                                        </span>
                                    </td>
                                    <td class="text-truncate" style="max-width: 180px;">
                                        <a :href="contents.youtubeUrl" target="_blank" class="text-left">
                                            {{ contents.youtubeUrl }}
                                        </a>
                                    </td>
                                    <td>{{ contents.timeStamp }}</td>
                                    <td class="text-truncate" style="max-width: 180px;">
                                        <a :href="contents.dropboxLink" target="_blank" class="text-left">
                                            {{ contents.dropboxLink }}
                                        </a>
                                    </td>
                                    <td>
                                        <!-- <MDBBtn class="btn-primary mt-md-0 mt-3" @click="toClaimsList">
                                            <img :class="invertClass" src="@/assets/copyright-white.png"
                                                alt="pex-copies-logo" style="margin-left: 2px;width: 14px;">
                                            CLAIM NOW
                                        </MDBBtn> -->
                                        <span style="color: var(--primary); font-weight: bold; cursor: pointer;">CLAIM
                                            NOW</span>
                                    </td>
                                </tr>
                            </template>
                        </template>
                    </tbody>
                </MDBTable>
            </MDBCardBody>
        </MDBCard>
    </VVVLayoutNew>
</template>

<script setup>
import { useImpersonateStore } from "@/store/impersonateStore";
import { convertToReadableTime, timeAgo } from "@/helpers/parseDate";
import { extractVideoId } from "@/helpers/youtubeScraper";
import { GetAverageIssuanceTime } from "@/services/Contents/GetAverageIssuanceTime";
import { GetContentsClaimStatusList } from "@/services/Contents/GetContentsClaimStatusList";
import { GetContentsClaimStatusOverview } from "@/services/Contents/GetContentsClaimStatusOverview";
import { GetContentsList } from "@/services/Contents/GetContentsList";
import { GetRevenueWidgets } from "@/services/Revenue/GetRevenueWidget";
import { GetVideoTutorialList } from "@/services/Tutorials/GetTutorialList";
import { useUserDetailStore } from "@/store/userDetailStore";
import VVVLayoutNew from "@/views/VVVNew/VVVLayoutNew.vue";
import { useClipboard, useEventListener, useStorage, useTitle } from "@vueuse/core";
import {
    MDBAlert, MDBBtn, MDBBadge, MDBCard, MDBCardBody, MDBCardHeader, MDBIcon, MDBListGroup, MDBListGroupItem,
    MDBTable
} from "mdb-vue-ui-kit";
import { storeToRefs } from "pinia";
import { computed, defineExpose, onMounted, ref, watch } from "vue";
import VueFeather from 'vue-feather';
import { useRouter, useRoute } from "vue-router";
const impersonateStore = useImpersonateStore();
const { isImpersonating, userImpersonateDetail } = storeToRefs(impersonateStore);

const { copy, copied } = useClipboard({ legacy: true })

watch(copied, (value) => {
    if (value) {
        alert("Copied");
    }
});

const loading = ref(false);
const issueList = ref([
    // { id: 1, name: "Active Claims", icon: "check-circle", color: '#28a745', badgeColor: 'warning', message: 'Hooray, new claims approved!' },
    { id: 2, name: "Issues", icon: "times-circle", color: '#dc3545', badgeColor: 'danger', message: 'Take action as soon as possible' },
    { id: 3, name: "Duplicate Claims", icon: "copy", color: '#fd7e14', badgeColor: '', message: '' },
    { id: 4, name: "Resolved Issues", icon: "tools", color: '#007bff', badgeColor: '', message: '' },
    { id: 5, name: "Submitted Claims", icon: "file-upload", color: '#6c757d', badgeColor: '', message: '' },
    { id: 6, name: "For Further Review", icon: "exclamation-triangle", color: '#d5d500', badgeColor: '', message: '' },
    // { id: 7, name: "Awaiting for 100 views", icon: "eye", color: '#ffc107', badgeColor: '', message: '' }, // Remove temporarily
    // { id: 8, name: "Awaiting for 1 view", icon: "hourglass-half", color: '#ffc107', badgeColor: '', message: '' }
]);
const response = ref([]);

const userDetailStore = useUserDetailStore();
const { userDetail, isUser, isAdmin, isSuperAdmin } = storeToRefs(userDetailStore);
const isAdminOrSuperAdmin = computed(() => isAdmin.value || isSuperAdmin.value);

const tableHeaders = computed(() => {
    const commonHeaders = [
        "Claim Request Timeline",
        "Channel",
        "Youtube URL",
        "Timestamp",
        "Take Action",
    ];

    return [...commonHeaders.slice(0, -1), "Clip Used", commonHeaders[commonHeaders.length - 1]];
});

const router = useRouter();
// const toClaimsList = () => {
//     router.push("/claims-list");
// };

const toClaimsList = () => {
    
    if (isImpersonating.value && userImpersonateDetail.value?.id) {
        router.push({
            path: "/claims-list",
            query: {
                userId: userImpersonateDetail.value.id,
                isImpersonation: 'true'
            }
        });
    } else {
        router.push("/claims-list");
    }
};

// ✅ NEW: Build route with impersonation params for Issues section
const buildClaimsListRoute = (claimStatusId) => {
    
    const baseRoute = `/claims-list?claimStatus=${claimStatusId}`;
    
    if (isImpersonating.value && userImpersonateDetail.value?.id) {
        const route = `${baseRoute}&userId=${userImpersonateDetail.value.id}&isImpersonation=true`;
        return route;
    }
    
    return baseRoute;
};
const toTutorials = () => {
    router.push(`/tutorials`);
};

const isLoading = ref(false);
const contentsList = ref([]);
const contentsListPagination = ref({});
const totalRevenue = ref(null);
const revenueLastMonth = ref(null);
const averageIssuanceTime = ref(null);
const latestTutorial = ref();
const isRevenueLoading = ref(true);
const isOverviewStatsLoading = ref(true);
const isTutorialLoading = ref(true);
const claimStatusOverview = ref([]);

// onMounted(async () => {
//     useTitle("Dashboard | Very Viral Videos");
//     await loadBreakdown();
//     await fetchContentsList()
//     isWidgetLoading.value = true;
//     totalRevenue.value = await getTotalRevenue();
//     revenueLastMonth.value = await getRevenueLastMonth();
//     claimStatusOverview.value = await getClaimStatusOverview();
//     latestTutorial.value = await getVideoTutorialList();
//     averageIssuanceTime.value = await getAverageIssuanceTime();
//     isWidgetLoading.value = false;
// });

onMounted(async () => {
  useTitle("Dashboard | Very Viral Videos");

   // ← ADD THIS: Validate impersonation state
  const route = useRoute(); // Make sure to import useRoute
  const hasImpersonationQuery = route.query.isImpersonation === 'true';
  
  // If impersonating but no query parameter, it's invalid - clear it
  if (isImpersonating.value && !hasImpersonationQuery) {
    console.log('Clearing invalid impersonation state');
    const impersonateStore = useImpersonateStore();
    impersonateStore.resetImpersonation();
    
    // Reload to reflect changes
    window.location.reload();
    return; // Stop further execution
  }

  const userId = isImpersonating.value
    ? userImpersonateDetail.value.id
    : userDetail.value.id;

  loadDashboard(userId);
});

const loadDashboard = async (userId) => {
  isRevenueLoading.value = true;
  isOverviewStatsLoading.value = true;
  isTutorialLoading.value = true;

  const revenueTask = (async () => {
    try {
      const [total, lastMonth] = await Promise.all([
        getTotalRevenue(userId),
        getRevenueLastMonth(userId),
      ]);
      totalRevenue.value = total ?? '0';
      revenueLastMonth.value = lastMonth ?? '0';
    } finally {
      isRevenueLoading.value = false;
    }
  })();

  const overviewStatsTask = (async () => {
    try {
      const overviewPromise = getClaimStatusOverview(userId);
      const issuancePromise = isAdminOrSuperAdmin.value
        ? getAverageIssuanceTime(userId)
        : Promise.resolve(null);
      const [overview, issuance] = await Promise.all([overviewPromise, issuancePromise]);
      claimStatusOverview.value = overview ?? [];
      averageIssuanceTime.value = issuance;
    } finally {
      isOverviewStatsLoading.value = false;
    }
  })();

  const tutorialTask = (async () => {
    try {
      latestTutorial.value = await getVideoTutorialList(userId);
    } finally {
      isTutorialLoading.value = false;
    }
  })();

  await Promise.all([
    loadBreakdown(userId),
    fetchContentsList(userId),
    revenueTask,
    overviewStatsTask,
    tutorialTask,
  ]);
};

// const fetchContentsList = async () => {
//     isLoading.value = true;
//     try {
//         const contentRes = await GetContentsList({ pageNumber: 1, rowsPerPage: 5 });
//         contentsList.value = contentRes.data;
//         contentsListPagination.value = contentRes.pagination;
//     } catch (error) {
//         console.error(error);
//     } finally {
//         isLoading.value = false;
//     }
// };
const fetchContentsList = async (userId) => {
  isLoading.value = true;
  try {
    const contentRes = await GetContentsList({
      pageNumber: 1,
      rowsPerPage: 5,
      userId,
      isImpersonation: isImpersonating.value,
    });
    contentsList.value = contentRes.data;
    contentsListPagination.value = contentRes.pagination;
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

// const loadBreakdown = async () => {
//     loading.value = true;
//     response.value = await GetContentsClaimStatusList();
//     loading.value = false;
// };
const loadBreakdown = async (userId) => {
  loading.value = true;
  try {
    response.value = await GetContentsClaimStatusList(
      userId,                // pass userId from onMounted
      isImpersonating.value  // impersonation flag
    );
  } catch (error) {
    console.error("❌ Error loading breakdown:", error);
  } finally {
    loading.value = false;
  }
};

const getCount = (id) => {
    const item = response.value.find(counter => counter.id === id);
    return item ? formatNumberWithCommas(item.count) : 0;
};

function formatNumberWithCommas(number) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const activeClaimsCount = computed(() => {
    const activeClaim = response.value?.find(counter => counter.id === 1);
    return activeClaim ? formatNumberWithCommas(activeClaim.count) : '0';
});

// const getTotalRevenue = async () => {
//     const response = await GetRevenueWidgets("RevenueTotal");
//     return formatNumberWithCommas(response);
// }
const getTotalRevenue = async (userId) => {
  try {
    const response = await GetRevenueWidgets(
      "RevenueTotal",
      userId,
      isImpersonating.value // ✅ include this flag
    );
    return formatNumberWithCommas(response);
  } catch (error) {
    console.error("Error fetching total revenue:", error);
  }
};
// const getRevenueLastMonth = async () => {
//     const response = await GetRevenueWidgets("RevenueMonth");
//     return formatNumberWithCommas(response);
// }
const getRevenueLastMonth = async (userId) => {
  try {
    const response = await GetRevenueWidgets(
      "RevenueMonth",
      userId,
      isImpersonating.value // ✅ include impersonation flag
    );
    return formatNumberWithCommas(response);
  } catch (error) {
    console.error("Error fetching revenue last month:", error);
  }
};

// const getVideoTutorialList = async () => {
//     try {
//         const response = await GetVideoTutorialList(1);
//         return response.data.find(item => !item.isDeleted);
//     } catch (error) {
//         console.error("Error fetching tutorials:", error);
//     }
// };
const getVideoTutorialList = async (userId) => {
  try {
    const response = await GetVideoTutorialList(userId);
    return response.data.find(item => !item.isDeleted);
  } catch (error) {
    console.error("Error fetching tutorials:", error);
  }
};
// const getClaimStatusOverview = async () => {
//     try {
//         const response = await GetContentsClaimStatusOverview();
//         return response;
//     } catch (error) {
//         console.error("Error fetching Claim Status Overview:", error);
//     }
// };
const getClaimStatusOverview = async (userId) => {
  try {
    const response = await GetContentsClaimStatusOverview({
      userId,
      isImpersonation: isImpersonating.value,
    });
    return response;
  } catch (error) {
    console.error("Error fetching Claim Status Overview:", error);
  }
};
const getAverageIssuanceTime = async (userId) => {
  try {
    const response = await GetAverageIssuanceTime({
      claimStatus: 1,
      userId,
      isImpersonation: isImpersonating.value,
    });
    return response;
  } catch (error) {
    console.error("Error fetching Average Issuance Time:", error);
  }
};
// const getAverageIssuanceTime = async () => {
//     try {
//         const response = await GetAverageIssuanceTime();
//         return response;
//     } catch (error) {
//         console.error("Error fetching Average Issuance Time:", error);
//     }
// };



const pendingClaims = computed(() => {
    const claim = claimStatusOverview.value?.find(status => status.name === 'Pending');
    return claim ? formatNumberWithCommas(claim.value) : '0';
});
const approvalRatePercentage = computed(() => {
    const claim = claimStatusOverview.value?.find(status => status.name === 'ApprovalRatePercentage');
    return claim?.value ?? '0';
});
const averageIssuanceTimeDisplay = computed(() => {
    if (averageIssuanceTime.value == null) {
        return '—';
    }
    return convertToReadableTime(averageIssuanceTime.value);
});

const getYoutubeVideoIframe = (videoLink) => {
    if (!videoLink) {
        return '';
    }

    const videoId = extractVideoId(videoLink);
    if (!videoId) {
        console.error("Could not extract video ID from link:");
        return '';
    }

    return `https://www.youtube.com/embed/${videoId}?rel=0&color=white&disablekb=1&modestbranding=1&cc_load_policy=1`;
};


defineExpose({ loadBreakdown });

const googleSheetAlert = useStorage('googleSheetAlert', true);
const revenueAlert = useStorage('revenueAlert', true);

useEventListener(window, 'reset-alerts', () => {
    googleSheetAlert.value = null;
    revenueAlert.value = null;
});

const setBadgeColor = (claimStatus) => {
    if (claimStatus === "1") {
        return 'success';
    } else if (claimStatus === "2") {
        return 'danger';
    } else {
        return 'warning';
    }
}

const stopImpersonation = () => {
  impersonateStore.isImpersonating = false;
  impersonateStore.userImpersonateDetail = {
    email: "",
    firstName: "",
    id: "",
    lastName: "",
    userTypeId: "",
    isDistributor: false,
    googleSheetLink: null,
  };
  router.push("/users");
};
</script>

<style scoped>
:root {
    --white: #ffffff;
    --primary: #223698;
    --secondary: #e2e6ef;
    --accent: #02061b;
    --blue: #1f87fc;
}

.text-primary {
    color: var(--primary)
}

.metrics-container {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
}

.metrics-container .card {
    flex: 1 1 calc(33.33% - 1rem);
}

@media (min-width: 768px) and (max-width: 1023px) {
    .metrics-container .card {
        flex: 1 1 calc(50% - 1rem);
    }

    .metrics-container .card:nth-child(3) {
        flex: 1 1 100%;
        justify-content: center;
        display: flex;
    }
}

/* Mobile: 1 card per row */
@media (max-width: 767px) {
    .metrics-container .card {
        flex: 1 1 100%;
    }
}

.btn-primary {
    background-color: var(--primary);
    display: flex;
    align-items: center;
    gap: 5px;
}

.badge.bg-primary {
    background-color: var(--primary) !important;
}

.spinner-grow {
    color: var(--accent);
}

.card {
    border-radius: 16px;
}

.card-header {
    color: black !important;
    font-family: 'GilroyBold', sans-serif;
    background-color: white;
}

.card-title {
    color: var(--primary);
}

.alert-message {
    width: 100%;
}

@media (max-width: 767px) {
    .counter {
        min-height: 80px;
        width: 100%;
    }

    .alert-message {
        width: 75%;
    }
}

.overlay {
    display: none;
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    border-radius: 16px;
    background: rgb(0 0 0 / 25%);
    z-index: 1;
}

.overlay .btn {
    background-color: var(--primary);
    color: white;
}

.counter:hover .card-body .overlay {
    display: grid;
    place-content: center;
}

td {
    text-wrap: nowrap;
    width: fit-content;
    text-align: left;
    font-size: 14px;
}

th {
    text-wrap: nowrap;
    width: fit-content;
    text-align: left;
    font-size: 14.5px;
}

.image-container {
    position: relative;
    display: inline-block;
}

.image-container .loader {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    width: 35px;
    height: 35px;
    border: 3px solid rgba(0, 0, 0, 0.1);
    border-top-color: var(--primary);
    border-radius: 50%;
    animation: BorderSpin 1s linear infinite;
}

.image-container img {
    display: block;
    width: 35px;
    height: 35px;
    border-radius: 50%;
}

.image-container-big .loader,
.image-container img {
    width: 135px;
    height: 135px;
}

.alert-container {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
}

@keyframes BorderSpin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.metric-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 0;
}

.metric-value {
    color: var(--primary);
    font-weight: bold;
}

.metric-value-skeleton {
    display: inline-block;
    width: 4.5rem;
    height: 1rem;
    border-radius: 4px;
    background: linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%);
    background-size: 200% 100%;
    animation: metric-shimmer 1.2s ease-in-out infinite;
}

.tutorial-loading,
.tutorial-empty {
    display: grid;
    place-content: center;
    min-height: 250px;
}

@keyframes metric-shimmer {
    0% {
        background-position: 100% 0;
    }

    100% {
        background-position: -100% 0;
    }
}
</style>
